import type {
  MenuItemResponse,
  CategoryResponse,
  AddOnResponse,
  AddOnCategoryResponse,
  AddOnVariant,
} from '@/features/menu/types';
import type { RestaurantResponse } from '@/features/restaurants/types';
import type { DealSectionResponse } from './types/deals';
import type { DiscountType } from '@/features/discounts/types';

export function toBoolean(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.toLowerCase() === 'true' || value === '1';
  if (typeof value === 'number') return value !== 0;
  return Boolean(value);
}

export function mapCategoryFromBackend(raw: Record<string, unknown>): CategoryResponse {
  const addOnCategoryRefs = Array.isArray(raw.addOnCategoryRefs)
    ? (raw.addOnCategoryRefs as Array<{ placeOrder?: number; addOnCategoryId: string }>)
    : undefined;
  return {
    ...raw,
    id: (raw.categoryId ?? raw.id) as string,
    position: (raw.displayOrder ?? raw.position ?? 0) as number,
    isActive: toBoolean(raw.active ?? raw.isActive),
    addOnCategoryRefs,
  } as CategoryResponse;
}

function mapMenuItemVariantsFromBackend(
  variants: Array<Record<string, unknown>> | null | undefined
): MenuItemResponse['variants'] {
  if (!Array.isArray(variants)) return undefined;
  return variants.map((v, i) => ({
    id: (v.variantId ?? v.id ?? `v-${i}`) as string,
    menuItemId: '',
    name: (v.name ?? '') as string,
    price: typeof v.price === 'number' ? v.price : 0,
    size: (v.size as string | null | undefined) ?? null,
    isAvailable: true,
    isDefault: false,
    position: i,
  }));
}

export function mapMenuItemFromBackend(item: Record<string, unknown>): MenuItemResponse {
  const id = (item.itemId ?? item.id) as string;
  const categoryId = item.categoryId as string;
  const active = toBoolean(item.active ?? item.isActive ?? item.isAvailable);
  const descEn = item.descriptionEnglish as string | undefined;
  const descDe = item.descriptionGerman as string | undefined;
  const imageUrls = item.imageUrls as string[] | undefined;
  const imageUrl = (item.imageUrl as string | undefined) ?? (Array.isArray(imageUrls) && imageUrls[0]) ?? undefined;
  const description = (item.description as string | undefined) ?? descEn ?? descDe;
  const basePrice =
    item.basePrice != null && typeof item.basePrice === 'number' ? item.basePrice : null;
  const variants = mapMenuItemVariantsFromBackend((item.variants as Array<Record<string, unknown>>) ?? undefined);
  const spiceLevel = item.spiceLevel as string | undefined;
  const addOnCategoryRefs = Array.isArray(item.addOnCategoryRefs)
    ? (item.addOnCategoryRefs as Array<{ placeOrder?: number; addOnCategoryId: string }>)
    : undefined;
  return {
    ...item,
    id,
    categoryId,
    itemNumber: (item.itemNumber as string | null | undefined) ?? null,
    isAvailable: active,
    isActive: active,
    description,
    descriptionEnglish: descEn,
    descriptionGerman: descDe,
    imageUrl,
    basePrice,
    variants,
    hasVariants: toBoolean(item.hasVariants),
    hasAddOns: toBoolean(item.hasAddOns),
    allergens: Array.isArray(item.allergens) ? (item.allergens as string[]) : [],
    spiceLevel: spiceLevel ?? undefined,
    isVegetarian: toBoolean(item.isVegetarian),
    isVegan: toBoolean(item.isVegan),
    isGlutenFree: toBoolean(item.isGlutenFree),
    isFeatured: toBoolean(item.isFeatured),
    addOnCategoryRefs,
    discountId: (item.discountId as string | null | undefined) ?? null,
    discountedPrice: item.discountedPrice != null && typeof item.discountedPrice === 'number' ? item.discountedPrice : null,
    discountBadge: (item.discountBadge as string | null | undefined) ?? null,
  } as unknown as MenuItemResponse;
}

export function mapAddOnFromBackend(raw: Record<string, unknown>): AddOnResponse {
  const id = (raw.addOnId ?? raw.id) as string;
  const variantsRaw = (raw.variants as Array<Record<string, unknown>>) ?? [];
  const variants: AddOnVariant[] = variantsRaw.map((v) => ({
    id: (v.variantId ?? v.id) as string,
    name: (v.name ?? '') as string,
    price: typeof v.price === 'number' ? v.price : 0,
  }));
  return {
    ...raw,
    id,
    restaurantId: (raw.restaurantId ?? '') as string,
    categoryId: (raw.categoryId ?? raw.category ?? '') as string,
    name: (raw.name ?? '') as string,
    description: (raw.description as string | null | undefined) ?? null,
    allergens: Array.isArray(raw.allergens) ? (raw.allergens as string[]) : [],
    allergenCategories: (raw.allergenCategories as AddOnResponse['allergenCategories']) ?? undefined,
    hasVariants: toBoolean(raw.hasVariants),
    variants: variants.length ? variants : null,
    basePrice: raw.basePrice != null && typeof raw.basePrice === 'number' ? raw.basePrice : null,
    isActive: toBoolean(raw.isActive ?? raw.active),
    createdAt: (raw.createdAt ?? '') as string,
    updatedAt: (raw.updatedAt ?? '') as string,
  } as AddOnResponse;
}

export function mapAddOnCategoryFromBackend(raw: Record<string, unknown>): AddOnCategoryResponse {
  return {
    ...raw,
    id: (raw.categoryId ?? raw.id) as string,
    restaurantId: (raw.restaurantId ?? '') as string,
    name: (raw.name ?? '') as string,
    description: (raw.description as string | null | undefined) ?? null,
    selectionGroup: ((raw.selectionGroup as string) ?? 'multiple') as 'single' | 'multiple',
    displayOrder: (raw.displayOrder ?? raw.position) as number | null | undefined,
    isActive: toBoolean(raw.isActive ?? raw.active),
    createdAt: (raw.createdAt ?? '') as string,
    updatedAt: (raw.updatedAt ?? '') as string,
  } as AddOnCategoryResponse;
}

type DayEntry = { day: string; isClosed?: boolean; ranges: Array<{ openTime?: string; closeTime?: string; open?: string; close?: string }> };

function toHoursArray(arr: unknown): DayEntry[] | null {
  if (Array.isArray(arr)) return arr as DayEntry[];
  if (arr && typeof arr === 'object') {
    return Object.entries(arr as Record<string, unknown>).map(([day, ranges]) => ({
      day: day.toUpperCase(),
      ranges: Array.isArray(ranges)
        ? (ranges as Array<Record<string, string>>).map((r) => ({ openTime: r.open, closeTime: r.close }))
        : [],
      isClosed: !ranges || (Array.isArray(ranges) && (ranges as unknown[]).length === 0),
    }));
  }
  return null;
}

export function normalizeTimeRangesByDay(
  arr: unknown
): RestaurantResponse['deliveryTimeRanges'] {
  const hoursArray = toHoursArray(arr);
  if (!hoursArray) return undefined;
  const out = hoursArray
    .filter((d): d is DayEntry & { day: string } => d != null && typeof d === 'object' && typeof d.day === 'string')
    .map((d) => ({
      day: d.day as 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY',
      isClosed: Boolean(d.isClosed),
      ranges: Array.isArray(d.ranges)
        ? d.ranges
          .filter((r) => r != null && typeof r === 'object')
          .map((r) => ({
            openTime: typeof r.openTime === 'string' ? r.openTime : '09:00',
            closeTime: typeof r.closeTime === 'string' ? r.closeTime : '22:00',
            minMinutes: typeof (r as Record<string, unknown>).minMinutes === 'number' ? (r as Record<string, unknown>).minMinutes as number : 0,
            maxMinutes: typeof (r as Record<string, unknown>).maxMinutes === 'number' ? (r as Record<string, unknown>).maxMinutes as number : 0,
          }))
        : [],
    }));
  return out.length ? (out as RestaurantResponse['deliveryTimeRanges']) : undefined;
}

export function mapDealFromBackend(raw: Record<string, unknown>): DealSectionResponse {
  const rawItems = Array.isArray(raw.items) ? (raw.items as Array<Record<string, unknown>>) : [];
  const items = rawItems.map((item) => {
    const base = mapMenuItemFromBackend(item);
    const id = (base.id || (item.menuItemId as string | undefined) || '') as string;
    const discountedPrice =
      typeof item.discountedPrice === 'number' ? item.discountedPrice : (base.basePrice ?? 0);
    return { ...base, id, discountedPrice };
  });
  const rawBxgy = raw.buyXGetY as Record<string, unknown> | undefined;
  const buyXGetY = rawBxgy
    ? {
        buyQuantity: typeof rawBxgy.buyQuantity === 'number' ? rawBxgy.buyQuantity : 2,
        getQuantity: typeof rawBxgy.getQuantity === 'number' ? rawBxgy.getQuantity : 1,
        getDiscountType: ((rawBxgy.getDiscountType as string) ?? 'PERCENTAGE') as 'PERCENTAGE' | 'FIXED_AMOUNT',
        getDiscountValue: typeof rawBxgy.getDiscountValue === 'number' ? rawBxgy.getDiscountValue : 100,
      }
    : undefined;
  return {
    discountId: (raw.discountId ?? raw.id ?? '') as string,
    name: (raw.name ?? '') as string,
    description: (raw.description as string | undefined) ?? undefined,
    discountType: ((raw.discountType ?? 'PERCENTAGE') as DiscountType),
    discountScope: (raw.discountScope as DealSectionResponse['discountScope']) ?? undefined,
    discountValue: typeof raw.discountValue === 'number' ? raw.discountValue : 0,
    buyXGetY,
    startTime: (raw.startTime ?? '') as string,
    endTime: (raw.endTime ?? '') as string,
    daysOfWeek: Array.isArray(raw.daysOfWeek) ? (raw.daysOfWeek as string[]) : [],
    allowedOrderTypes: Array.isArray(raw.allowedOrderTypes) ? (raw.allowedOrderTypes as string[]) : [],
    imageUrl: (raw.imageUrl as string | null | undefined) ?? null,
    items,
  };
}

export function mapRestaurantFromBackend(raw: Record<string, unknown>): RestaurantResponse {
  const payload =
    raw.restaurant && typeof raw.restaurant === 'object' && !Array.isArray(raw.restaurant)
      ? (raw.restaurant as Record<string, unknown>)
      : raw;
  const closeMessage = (payload.closeMessage ?? payload.closedReason) as string | undefined;
  const isClosedRaw = payload.closed ?? payload.isClosed;
  const hasDeliveryVal = payload.hasDelivery ?? (payload as Record<string, unknown>).isHasDelivery;
  const hasPickupVal = payload.hasPickup ?? (payload as Record<string, unknown>).isHasPickup;
  const storeOpen =
    payload.storeOpen ?? payload.isStoreOpen ?? (isClosedRaw === false ? true : undefined);
  const deliveryOpen =
    payload.deliveryOpen ?? payload.isDeliveryOpen ?? (isClosedRaw === false && hasDeliveryVal ? true : undefined);
  const defaultLanguage = (payload.defaultLanguage ?? payload.default_language ?? 'en') as string;
  const isClosed = isClosedRaw;
  const deliveryZones = Array.isArray(payload.deliveryZones) ? (payload.deliveryZones as RestaurantResponse['deliveryZones']) : undefined;
  let deliveryFee = typeof payload.deliveryFee === 'number' ? payload.deliveryFee : undefined;
  let deliveryMinOrderAmount = typeof payload.deliveryMinOrderAmount === 'number' ? payload.deliveryMinOrderAmount : undefined;
  if (deliveryZones?.length) {
    if (deliveryFee === undefined) deliveryFee = Math.min(...deliveryZones.map((z) => z.deliveryFee));
    if (deliveryMinOrderAmount === undefined) deliveryMinOrderAmount = Math.max(...deliveryZones.map((z) => z.minOrderAmount), 0);
  }
  const addr = payload.address && typeof payload.address === 'object' ? (payload.address as Record<string, unknown>) : null;
  const addrStr = (v: unknown) => (typeof v === 'string' ? v : undefined);
  const streetVal = addr ? (addrStr(addr.street) ?? addrStr(addr.street_name)) : undefined;
  const streetNumVal = addr ? (addrStr(addr.streetNumber) ?? addrStr(addr.street_number)) : undefined;
  const postalVal = addr ? (addrStr(addr.postalCode) ?? addrStr(addr.postal_code) ?? addrStr(addr.zipCode) ?? addrStr(addr.zip_code)) : undefined;
  const cityVal = addr ? addrStr(addr.city) : undefined;
  const countryVal = addr ? addrStr(addr.country) : undefined;
  return {
    ...payload,
    id: (payload.restaurantId ?? payload.id) as string,
    name: (payload.title ?? payload.name) as string,
    description: payload.description as string | undefined,
    subHeading: payload.subHeading as string | undefined,
    slug: payload.slug as string | undefined,
    logoUrl: payload.logoUrl as string | undefined,
    coverImageUrl: payload.coverImageUrl as string | undefined,
    themeColor: payload.themeColor as string | undefined,
    galleryPhotos: (payload.gallery ?? payload.galleryPhotos) as string[] | undefined,
    contactEmail: payload.contactEmail as string | undefined,
    contactPhone: payload.contactPhone as string | undefined,
    street: (streetVal ?? payload.street) as string | undefined,
    streetNumber: (streetNumVal ?? payload.streetNumber) as string | undefined,
    houseNumber: (streetNumVal ?? payload.streetNumber) as string | undefined,
    postalCode: (postalVal ?? payload.postalCode) as string | undefined,
    city: (cityVal ?? payload.city) as string | undefined,
    country: (countryVal ?? payload.country) as string | undefined,
    latitude: typeof payload.latitude === 'number' ? payload.latitude : undefined,
    longitude: typeof payload.longitude === 'number' ? payload.longitude : undefined,
    currency: payload.currency as string | undefined,
    customDomain: payload.customDomain as string | undefined,
    socialLinks: payload.socialLinks as Record<string, string> | undefined,
    languagesSupported: Array.isArray(payload.languagesSupported) ? (payload.languagesSupported as string[]) : undefined,
    paymentMethods: Array.isArray(payload.paymentMethods) ? (payload.paymentMethods as string[]) : undefined,
    seoMetaTitle: payload.seoMetaTitle as string | undefined,
    seoMetaDescription: payload.seoMetaDescription as string | undefined,
    seoKeywords: Array.isArray(payload.seoKeywords) ? (payload.seoKeywords as string[]) : (payload.seoKeywords as string | undefined),
    isActive: toBoolean(payload.active ?? payload.isActive),
    isStoreOpen: toBoolean(storeOpen),
    isDeliveryOpen: toBoolean(deliveryOpen),
    isClosed: toBoolean(isClosed),
    hasPickup: toBoolean(hasPickupVal),
    hasDelivery: toBoolean(hasDeliveryVal),
    isVerified: toBoolean(payload.verified ?? payload.isVerified),
    isDomainVerified: toBoolean(payload.domainVerified ?? payload.isDomainVerified),
    closeMessage,
    closedReason: closeMessage,
    defaultLanguage,
    deliveryZones,
    deliveryFee,
    deliveryMinOrderAmount,
    openingHours: payload.openingHours as RestaurantResponse['openingHours'],
    deliveryHours: payload.deliveryHours as RestaurantResponse['deliveryHours'],
    ownerName: payload.ownerName as string | undefined,
    taxNumber: payload.taxNumber as string | undefined,
    vatNumber: payload.vatNumber as string | undefined,
    businessNumber: payload.businessNumber as string | undefined,
    impressum: payload.impressum as string | undefined,
    datenschutz: payload.datenschutz as string | undefined,
    agb: payload.agb as string | undefined,
    widerruf: payload.widerruf as string | undefined,
    cookie: payload.cookie as string | undefined,
    odr: payload.odr as string | undefined,
    alkohol: payload.alkohol as string | undefined,
    legalPages: payload.legalPages as Record<string, string> | undefined,
    deliveryTimeRanges: normalizeTimeRangesByDay(payload.deliveryTimeRanges),
    pickupTimeRanges: normalizeTimeRangesByDay(payload.pickupTimeRanges),
  } as RestaurantResponse;
}
